import { useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook for displaying toast notifications using sonner
 * This replaces notistack's useSnackbar
 *
 * Usage:
 * ```tsx
 * const { showSuccess, showError, showWarning, showInfo } = useToast();
 *
 * showSuccess('Operation completed');
 * showError('Something went wrong');
 * showWarning('Please check your input');
 * showInfo('New update available');
 * ```
 */
export function useToast() {
  const showSuccess = useCallback((message: string, description?: string) => {
    toast.success(message, { description });
  }, []);

  const showError = useCallback((message: string, description?: string) => {
    toast.error(message, { description });
  }, []);

  const showWarning = useCallback((message: string, description?: string) => {
    toast.warning(message, { description });
  }, []);

  const showInfo = useCallback((message: string, description?: string) => {
    toast.info(message, { description });
  }, []);

  // Legacy API compatibility with notistack
  const enqueueSnackbar = useCallback(
    (
      message: string,
      options?: { variant?: 'success' | 'error' | 'warning' | 'info' | 'default' },
    ) => {
      const variant = options?.variant || 'default';
      switch (variant) {
        case 'success':
          toast.success(message);
          break;
        case 'error':
          toast.error(message);
          break;
        case 'warning':
          toast.warning(message);
          break;
        case 'info':
          toast.info(message);
          break;
        default:
          toast(message);
      }
    },
    [],
  );

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    enqueueSnackbar,
    toast,
  };
}

export default useToast;
